import { useTranslation } from "react-i18next";
import { Copy, Icon, Navigation } from "../../components";
import { Box, PageContainer, Typography } from "../../shared/ui";
import css from "./MyBetInner.module.scss";
import { useProvablyFairRound } from "./useProvablyFairRound";

export const MyBetInner = ({ roundId }: { roundId: number }) => {
  const { data } = useProvablyFairRound(roundId, { auto: true });
  const { t } = useTranslation();
  const date = data?.timestamp ? new Date(Number(data.timestamp)) : null;
  const dateStr = date ? date.toLocaleDateString() : "-";
  const timeStr = date ? date.toLocaleTimeString() : "-";

  // До раскрытия раунда бэкенд намеренно не отдаёт server seed, crash point и
  // производные от них поля. `revealed` объявлен без `optional`, поэтому у
  // старого бэкенда декодируется в `false` — подстраховываемся наличием seed,
  // чтобы не показать «не раскрыто» для уже раскрытого раунда.
  const isRevealed = Boolean(data?.revealed || data?.serverSeed);
  const isLoaded = data != null;

  return (
    <div>
      <Navigation
        title={t("my_bets.round", { roundId })}
        badge={
          <div className={css.badge}>
            <Typography className={css.badgeText} color="purple" variant="h2">
              {data?.crashPoint != null
                ? `${Number(data.crashPoint).toFixed(2)}x`
                : "-"}
            </Typography>
          </div>
        }
      />

      <PageContainer>
        <Box className={css.dateContainer}>
          <Box display="flex" align="center">
            <Icon name="calendar" width={20} height={20} color="#707071" />
            <Typography
              className={css.date}
              color="light-grey"
              variant="caption2"
            >
              {dateStr}
            </Typography>
          </Box>
          <Box display="flex" align="center">
            <Icon name="clock" width={20} height={20} color="#707071" />
            <Typography
              className={css.date}
              color="light-grey"
              variant="caption2"
            >
              {timeStr}
            </Typography>
          </Box>
        </Box>

        {isLoaded && !isRevealed ? (
          <div className={css.pendingReveal}>
            <Typography color="light-grey" className={css.descr}>
              {t("my_bets.not_revealed_yet")}
            </Typography>
          </div>
        ) : null}

        <Box display="flex" direction="column" width="100%" gap="lg">
          <Box display="flex" direction="column" align="start">
            <Typography className={css.title} color="header-small">
              {t("my_bets.server_seed")}
            </Typography>
            <Typography className={css.descr} color="light-grey">
              {t("my_bets.generated_on_our_side")}
            </Typography>
            <Copy copyText={data?.serverSeed || "-"} />
          </Box>

          <Box display="flex" direction="column" align="start">
            <Typography className={css.title} color="header-small">
              {t("my_bets.public_hash")}
            </Typography>
            <Typography className={css.descr} color="light-grey">
              {t("my_bets.server_seed_hashed")}
            </Typography>
            <Copy copyText={data?.publicHash || "-"} />
          </Box>

          <Box display="flex" direction="column" align="start">
            <Typography className={css.title} color="header-small">
              {t("my_bets.client_seeds")}
            </Typography>
            <Typography className={css.descr} color="light-grey">
              {t("my_bets.generated_on_players_side")}
            </Typography>
            <Box display="flex" direction="column" gap="sm" width="100%">
              {(data?.clientSeeds ?? []).slice(0, 3).map((seed, idx) => (
                <Copy key={idx} copyText={seed} />
              ))}
            </Box>
          </Box>

          <Box display="flex" direction="column" align="start">
            <Typography className={css.title} color="header-small">
              {t("my_bets.combined_sha_256_hash")}
            </Typography>
            <Typography className={css.descr} color="light-grey">
              {t("my_bets.above_seeds_combined")}
            </Typography>
            <Box display="flex" direction="column" gap="sm" width="100%">
              <Copy copyText={data?.combinedHash || "-"} />
              <Copy
                firstItem={
                  <Typography color="light-grey" className={css.type}>
                    {t("my_bets.hex")}
                  </Typography>
                }
                copyText={data?.hex || "-"}
              />
              <Copy
                firstItem={
                  <Typography color="light-grey" className={css.type}>
                    {t("my_bets.dec")}
                  </Typography>
                }
                copyText={String(data?.dec ?? "-")}
              />
              <Copy
                firstItem={
                  <Typography color="light-grey" className={css.type}>
                    {t("my_bets.odds")}
                  </Typography>
                }
                copyText={data?.odds != null ? String(data.odds) : "-"}
              />
            </Box>
          </Box>
        </Box>
      </PageContainer>
    </div>
  );
};
